import "@/styles/Layout.scss";
import { useForm } from "react-hook-form";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useParams, useNavigate } from "react-router-dom";
import Spinner from "@/components/Spinner";
import { useEffect, useState } from "react";
import getProduct from "@/scripts/getProduct";
import Logo from "../assets/nav-logo.svg";
import ImageViewer from "@/components/ImageViewer";
import { Copy } from "lucide-react";
import WhatsappIcon from "../assets/whatsapp.svg";


function ViewProduct() {
  const { type, id } = useParams();
  const navigate = useNavigate();

  const [data, setData] = useState({});
  const [product, setProduct] = useState({});
  const [loading, setLoading] = useState(true);

  // Initialize the form with empty values first
  const form = useForm({
    defaultValues: {
      grade: "",
      form: "",
      price: "",
      quantity: "",
      location: "",
      source: "",
      postalCode: "",
      priceUnit: "",
      description: "",
      quantityUnit: "",
    },
  });
  
  const copyEmail = (email) => {
    navigator.clipboard.writeText(email);
    alert("Email copied to clipboard!");
  }

  const openWhatsapp = (phone) => {
    const phoneWithoutPlus = phone.replace(/\+/g, "");
    const url = `https://wa.me/${phoneWithoutPlus}`;
    window.open(url, "_blank", "noopener,noreferrer");
  }

  const fetchProduct = async () => {
    try {
      setLoading(true);
      const data = await getProduct(type, id);
      setData(data);
      setProduct(data.product);

      // Reset form with fetched product data
      form.reset({
        grade: data.product?.grade || "",
        form: data.product?.form || "",
        price: `${data.product?.price} ${data.product?.priceUnit}` || "",
        quantity: `${data.product?.quantity} ${data.product?.quantityUnit}` || "",
        location: data.product?.location || "",
        source: data.product?.source || "",
        postalCode: data.product?.postalCode || "",
        priceUnit: data.product?.priceUnit || "",
        description: data.product?.description || "",
        quantityUnit: data.product?.quantityUnit || "",
        agentName: [data.agent?.first_name, data.agent?.last_name]
          .filter(Boolean)
          .join(" ") || "",
        agentPhone: data.agent?.phone || "",
        agentEmail: data.agent?.email || "",
      });
    } catch (error) {
      console.log("Error fetching data", error);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchProduct();
  }, []);

  if (loading) {
    return <Spinner />;
  }

  return (
    <div className="flex-grow">
      <nav className="px-0 md:px-6 py-4 bg-black shadow-md">
        <div className="container mx-auto flex justify-between items-center">
          <div
            onClick={() => navigate("/")}
            className="flex justify-between items-center cursor-pointer"
          >
            <img src={Logo} alt="JP Associates" className="h-11 w-11 mr-1" />
            <div className="text-md font-bold text-green">JP Associates</div>
          </div>
          
          <button
            onClick={() => navigate("/login")}
            className="px-4 py-2 bg-[#17be73] text-white text-sm rounded-md hover:bg-green-600 hover:text-white transition"
          >
            Sign in
          </button>
        </div>
      </nav>

      <div className="sectionWrapper mobileWrapper">
        <h1 className="text-md font-bold text-green ml-3 mt-2">{type.toUpperCase()} REQUEST / {data.reference_id.toUpperCase()}</h1>

        <p className="mt-3 ml-3 text-sm text-muted">
          Explore all the details about the product on this page.
        </p>

        <Form {...form}>
          <form className="space-y-4 p-3">
            <div className="flex justify-between">
              {/* Grade */}
              <div className="w-[49%]">
                <FormField
                  control={form.control}
                  name="grade"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Grade of Material</FormLabel>
                      <FormControl className="bg-white">
                        <Input type="text" {...field} disabled />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Form */}
              <div className="w-[49%]">
                <FormField
                  control={form.control}
                  name="form"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Form of Material</FormLabel>
                      <FormControl className="bg-white">
                        <Input type="text" {...field} disabled />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            <div className="flex justify-between">
              {/* Price */}
              {product.price && (
                <div className="w-[49%]">
                  <FormField
                    control={form.control}
                    name="price"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Price</FormLabel>
                        <FormControl className="bg-white">
                          <Input type="text" {...field} disabled />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              )}

              {/* Quantity */}
              {product.quantity && (
                <div className="w-[49%]">
                  <FormField
                    control={form.control}
                    name="quantity"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Quantity</FormLabel>
                        <FormControl className="bg-white">
                          <Input type="text" {...field} disabled />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              )}
            </div>

            <div className="flex justify-between">
              {/* Postal Code */}
              {product.postalCode && (
                <div className="w-[49%]">
                  <FormField
                    control={form.control}
                    name="postalCode"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Postal Code</FormLabel>
                        <FormControl className="bg-white">
                          <Input type="text" {...field} disabled />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              )}

              {/* Location */}
              {product.location && (
                <div className="w-[49%]">
                  <FormField
                    control={form.control}
                    name="location"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Location</FormLabel>
                        <FormControl className="bg-white">
                          <Input type="text" {...field} disabled />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              )}
            </div>

            {/* Description */}
            {product.description && (
              <div className="flex justify-between">
                <div className="w-[100%]">
                  <FormField
                    control={form.control}
                    name="description"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Description</FormLabel>
                        <FormControl>
                          <Textarea {...field} disabled />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>
            )}

            {/* Source of inquiry */}
            {product.source && (
              <div className="flex justify-between">
                <div className="w-[100%]">
                  <FormField
                    control={form.control}
                    name="source"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Source of inquiry</FormLabel>
                        <FormControl>
                          <Input {...field} disabled />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>
            )}

            {/* Agent Details */}
            <div>
              <h3 className="text-sm font-semibold mb-2 mt-6">Agent Information</h3>

              <div className="flex justify-between">
                <div className="w-[49%]">
                  <FormField
                    control={form.control}
                    name="agentName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Name</FormLabel>
                        <FormControl className="bg-white">
                          <Input type="text" {...field} disabled />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="w-[49%]">
                  <FormField
                    control={form.control}
                    name="agentPhone"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Phone</FormLabel>
                        <FormControl>
                          <div className="relative">
                            <Input type="text" {...field} disabled className="pr-10" />

                            <img
                              src={WhatsappIcon}
                              alt="whatsapp"
                              className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 cursor-pointer"
                              onClick={() => openWhatsapp(field.value)}
                            />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              {/* Email full width */}
              <div className="mt-3">
                <FormField
                  control={form.control}
                  name="agentEmail"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Input type="text" {...field} disabled className="pr-10" />

                          <Copy
                            className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 cursor-pointer"
                            onClick={() => copyEmail(field.value)}
                          />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            <div className="mt-2">
              <FormLabel className="mt-2 font-semibold">Product Images</FormLabel>
              <div className="productWrapper">
                {
                  product.images.map((image, idx) => 
                    <div key={idx}>
                      <div className="productCard cursor-pointer">
                        <ImageViewer src={image.url} alt={image?.description} />
                        <p className="p-2 text-sm text-muted break-words overflow-hidden">
                          {image?.description}
                        </p>
                      </div>
                    </div>
                  )
                }
              </div>
            </div>
          </form>
        </Form>
      </div>
    </div>
  );
}

export default ViewProduct;
